//Custom css import
import './style.css';

import { EmptyArray } from '../../../app/domain/models/empty/empty';

//React import
import { useContext, useEffect, useState, useMemo } from 'react';

import Table from '../../molecules/tablas/view';
import FacetedFilter from '../../molecules/FacetedFilter/view';
import NewSpend from '../NewSpend/view';
import ListItemsGasto from '../listItemsGasto/view';
import RadianEventsModal from '../RadianEventsModal/view';

import ButtonAction from '../../atoms/buttonAction/view';
import ConfirmPopUp from '../../atoms/confirmPopUp/view';

import { getSpends, deleteSpend } from '../../../usecases/spend-usecase';
import { listener, dispatchCustomEvent } from '../../../utils/dispatch';
import ShowThird from '../showThird/view';
import { roleContext } from '../../../context/role/context';
import { Trash2, Zap, Calendar, User } from 'lucide-react';

// ─── Botón de acción: Eliminar Gasto ──────────────────────────────
function DeleteSpendBtn({ data }) {
    return (
        <ButtonAction
            btnKey="delete-spend"
            onClick={() => dispatchCustomEvent('btn-notification-confirm-delete-spend', data)}
            title={<Trash2 size={14} />}
            style={{ width: '100%' }}
        />
    );
}

const toISODateString = (dateVal) => {
    if (!dateVal) return '';
    const str = String(dateVal).trim();
    const isoMatch = str.match(/(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
    if (isoMatch) {
        const year = isoMatch[1];
        const month = isoMatch[2].padStart(2, '0');
        const day = isoMatch[3].padStart(2, '0');
        return `${year}-${month}-${day}`;
    }
    const dmyMatch = str.match(/(\d{1,2})[-/](\d{1,2})[-/](\d{4})/);
    if (dmyMatch) {
        const day = dmyMatch[1].padStart(2, '0');
        const month = dmyMatch[2].padStart(2, '0');
        const year = dmyMatch[3];
        return `${year}-${month}-${day}`;
    }
    const d = new Date(dateVal);
    if (!isNaN(d.getTime())) {
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }
    return '';
};

// ─── SpendTable ───────────────────────────────────────────────────────────────
function SpendTable({ ref, profile }) {

    const [loading, setLoading] = useState(false);
    const [radianModal, setRadianModal] = useState(null);
    const [activeFilters, setActiveFilters] = useState([]);

    const filtersConfig = [
        { key: 'date_from', label: 'Fecha Desde', icon: <Calendar size={14} />, type: 'date' },
        { key: 'date_to', label: 'Fecha Hasta', icon: <Calendar size={14} />, type: 'date' },
        { key: 'third', label: 'Tercero', icon: <User size={14} />, type: 'text' }
    ];

    const [spendsData, setSpendsData] = useState({
        data: [],
        page: 1,
        page_size: 10,
        total: 0
    });

    const filteredSpends = useMemo(() => {
        let list = spendsData.data || [];
        const dateFrom = activeFilters.find(f => f.key === 'date_from')?.value;
        const dateTo = activeFilters.find(f => f.key === 'date_to')?.value;
        const thirdVal = activeFilters.find(f => f.key === 'third')?.value;

        if (dateFrom) {
            list = list.filter(item => {
                const itemDate = toISODateString(item.raw_date);
                return itemDate ? itemDate >= dateFrom : true;
            });
        }
        if (dateTo) {
            list = list.filter(item => {
                const itemDate = toISODateString(item.raw_date);
                return itemDate ? itemDate <= dateTo : true;
            });
        }
        if (thirdVal) {
            const q = thirdVal.toLowerCase().trim();
            list = list.filter(item => {
                const tName = (item.third_id?.third_name || item.tercero?.name || item.third_id?.name || '').toLowerCase();
                const tId = (item.tercero?.identification || item.third_id?.tercero?.identification || '').toLowerCase();
                const doc = (item.document || '').toLowerCase();
                return tName.includes(q) || tId.includes(q) || doc.includes(q);
            });
        }
        return list;
    }, [spendsData.data, activeFilters]);



    const handleOpen = (page = spendsData.page, limit = spendsData.page_size) => {
        setLoading(true);
        setSpendsData(prev => ({ ...prev, data: [] }));
        getSpends(page, limit).then((res) => {
            const spendsList = res.data?.data || res.data || [];
            if (!Array.isArray(spendsList) || spendsList.length === 0) {
                setSpendsData({ data: EmptyArray, page, page_size: limit, total: 0 });
            } else {
                const mapped = spendMapper(spendsList);
                setSpendsData({
                    data: mapped,
                    page: res.page || page,
                    page_size: res.page_size || limit,
                    total: res.total || mapped.length
                });
            }
        }).catch(() => {
            setSpendsData({ data: EmptyArray, page, page_size: limit, total: 0 });
        }).finally(() => {
            setLoading(false);
        });
    };

    useEffect(() => {
        handleOpen(1, 10);
    }, []);

    useEffect(() => {
        const editListener = listener('spend-edited', () => handleOpen());
        const addListener = listener('spend-added', () => handleOpen(1, 10));
        const reloadListener = listener('reload-spends', () => handleOpen());

        return () => {
            editListener();
            addListener();
            reloadListener();
        };
    }, []);

    async function handleDeleteActual(data) {
        if (!data?.id) return;
        try {
            await deleteSpend(data.id);
            dispatchCustomEvent('popup', {
                message: 'Gasto eliminado correctamente',
                type: 'success'
            });
            handleOpen(); // Actualiza la tabla inmediatamente
        } catch (error) {
            const msg = error?.response?.data?.message
                || error?.message
                || 'Error al eliminar el gasto';
            dispatchCustomEvent('popup', { message: msg, type: 'error' });
        } finally {
            dispatchCustomEvent('loading-btn', {});
        }
    }

    function spendMapper(data) {
        if (!Array.isArray(data)) return [];
        return data.map((item) => {
            const grossAmount = (item.service_lines || []).reduce((acc, line) => acc + parseFloat(line.amount || 0), 0) || parseFloat(item.amount || 0);
            const retentionAmount = (item.service_lines || []).reduce((acc, line) => acc + parseFloat(line.retention || 0), 0) || parseFloat(item.retention || 0);
            const netTotal = grossAmount - retentionAmount;

            const formatDate = (dateStr) => {
                if (!dateStr) return 'N/A';
                const d = new Date(dateStr);
                return isNaN(d.getTime()) ? dateStr : d.toLocaleDateString('es-CO');
            };

            const supplierName = item.tercero?.name || item.third_name || (item.third_id ? `Tercero #${item.third_id}` : 'Proveedor General');

            return {
                date: formatDate(item.spend_date || item.created_at),
                raw_date: item.spend_date || item.created_at,
                document: item.documento_compra ? `#${item.id} (${item.documento_compra})` : `#${item.id}`,
                third_id: {
                    third_id: item.third_id,
                    third_name: supplierName,
                    tercero: item.tercero
                },
                total: netTotal,

                // Excluded properties for details and actions:
                id: item.id,
                service_lines: item.service_lines || [],
                payment_methods: item.payment_methods || [],
                retention: retentionAmount,
                tax: item.tax || 0,
                amount: grossAmount,
                created_at: item.created_at,
                spend_date: item.spend_date,
                tercero: item.tercero,
                xml_invoice_data: item.xml_invoice_data
            };
        });
    }

    return (
        <>
            {radianModal && (
                <RadianEventsModal
                    xmlInvoiceData={radianModal}
                    onClose={() => setRadianModal(null)}
                />
            )}
            <ConfirmPopUp
                eventKey="delete-spend"
                msg="¿Estás seguro de eliminar este gasto? Esta acción eliminará también el asiento contable y la cuenta por pagar relacionada."
                onAcept={handleDeleteActual}
            />

            <Table
                id="spend-table"
                isLoading={loading}
                emptyMessage="Aún no tienes gastos"
                emptyChild={<NewSpend id="spend-table" profile={profile}></NewSpend>}
                headersList={["Fecha", "Documento", "Proveedor", "Total"]}
                paginatedData={{ ...spendsData, data: filteredSpends }}
                getFunction={handleOpen}
                allowExport={true}
                exportFileName="gastos"
                allowPageSizeControl={true}
                toolbarActions={
                    <FacetedFilter
                        filters={filtersConfig}
                        activeFilters={activeFilters}
                        onChange={setActiveFilters}
                        onClear={() => setActiveFilters([])}
                    />
                }
                excludeList={[
                    "raw_date",
                    "id",
                    "service_lines",
                    "payment_methods",
                    "retention",
                    "tax",
                    "amount",
                    "created_at",
                    "spend_date",
                    "tercero",
                    "xml_invoice_data"
                ]}
                formats={{
                    third_id: {
                        type: "custom",
                        children: <ShowThird profile={profile} id="spend-table" />
                    },
                    total: { type: 'money' }
                }}
            >
                <ListItemsGasto profile={profile} />
                <ButtonAction
                    btnKey="radian-spend"
                    title={<Zap size={15} />}
                    style={{ color: '#f59e0b' }}
                    hidden={(data) => !data?.xml_invoice_data}
                    onClick={(data) => {
                        let xmlData = data?.xml_invoice_data;
                        if (typeof xmlData === 'string') {
                            try {
                                xmlData = JSON.parse(xmlData);
                            } catch (_err) {
                                // Ignorar error si el string no es JSON válido
                            }
                        }
                        if (!xmlData) {
                            dispatchCustomEvent('popup', {
                                message: 'Este gasto no cuenta con datos de factura electrónica XML para emitir eventos RADIAN.',
                                type: 'warning'
                            });
                            return;
                        }
                        setRadianModal(xmlData);
                    }}
                />
                <DeleteSpendBtn />
            </Table>
        </>
    );

}

export default SpendTable;


